import React from 'react';
import { MessageCircle, MoreHorizontal, Edit2, Trash2, MessageSquare } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Thread } from '../../types';
import { useChatStore } from '../../store/chatStore';
import { apiService } from '../../services/api';
import RenameThreadModal from './RenameThreadModal';
import DeleteThreadModal from './DeleteThreadModal';

// Fallback title for threads without a title (makes it easy to debug by searching for this constant)
const THREAD_NO_TITLE_FALLBACK = 'Untitled Thread';

interface ThreadItemProps {
  thread: Thread;
  isSelected: boolean;
  onClick: () => void;
  onRename: (threadId: string, title: string) => void;
  onDelete: (threadId: string, title: string) => void;
}

const ThreadItem: React.FC<ThreadItemProps> = ({ 
  thread, 
  isSelected, 
  onClick, 
  onRename, 
  onDelete 
}) => {
  const [showActions, setShowActions] = React.useState(false);
  const [showDropdown, setShowDropdown] = React.useState(false);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setShowDropdown(false);
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  return (
    <div
      className={`group relative py-0.5 px-2 rounded-lg cursor-pointer transition-all duration-200 ${
        isSelected
          ? 'bg-gray-50 border border-transparent'
          : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'
      }`}
      onClick={onClick}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => { if (!showDropdown) setShowActions(false); }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <MessageCircle className={`h-3 w-3 flex-shrink-0 ${isSelected ? 'text-purple-500' : 'text-gray-400'}`} />
            <h4
              className={`text-sm font-medium truncate ${
                isSelected 
                  ? 'text-purple-700' 
                  : thread.unread 
                    ? 'text-gray-900' 
                    : 'text-gray-700'
              }`}
              title={thread.title || THREAD_NO_TITLE_FALLBACK}
            >
              {thread.title || THREAD_NO_TITLE_FALLBACK}
            </h4>
            {thread.unread && (
              <div className="w-2 h-2 bg-primary-600 rounded-full flex-shrink-0" />
            )}
          </div>
        </div>

        {showActions && (
          <div className="absolute top-2 right-2">
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdown(!showDropdown);
                }}
                className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreHorizontal className="h-3 w-3" />
              </button>

              {showDropdown && (
                <div
                  className="absolute right-0 z-10 mt-1 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
                  onMouseEnter={() => setShowActions(true)}
                  onMouseLeave={() => setShowActions(false)}
                >
                  <div className="py-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRename(thread.id, thread.title || THREAD_NO_TITLE_FALLBACK);
                        setShowDropdown(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Edit2 className="h-3 w-3 mr-2" />
                      Rename
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(thread.id, thread.title || THREAD_NO_TITLE_FALLBACK);
                        setShowDropdown(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3 mr-2" />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ThreadList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentThread } = useChatStore();
  
  // Local state for unfiled threads
  const [threads, setThreads] = React.useState<Thread[]>([]);
  const [isLoadingThreads, setIsLoadingThreads] = React.useState(false);

  // Modal states
  const [showRenameModal, setShowRenameModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [selectedThreadId, setSelectedThreadId] = React.useState<string | undefined>();
  const [selectedThreadTitle, setSelectedThreadTitle] = React.useState<string | undefined>();

  // Function to load unfiled threads
  const loadUnfiledThreads = React.useCallback(async () => {
    setIsLoadingThreads(true);
    try {
      const unfiledThreads = await apiService.getThreads(true); // true = no_folder
      setThreads(unfiledThreads);
    } catch (error) {
      console.error('Failed to load unfiled threads:', error);
    } finally {
      setIsLoadingThreads(false);
    }
  }, []);

  // Load unfiled threads on component mount
  React.useEffect(() => {
    loadUnfiledThreads();
  }, [loadUnfiledThreads]);

  // Refresh thread list when location changes (new thread created)
  React.useEffect(() => {
    loadUnfiledThreads();
  }, [location.pathname, loadUnfiledThreads]);

  const handleThreadClick = (thread: Thread) => {
    navigate(`/chat/${thread.id}`);
  };

  const handleRename = (threadId: string, title: string) => {
    setSelectedThreadId(threadId);
    setSelectedThreadTitle(title);
    setShowRenameModal(true);
  };

  const handleDelete = (threadId: string, title: string) => {
    setSelectedThreadId(threadId);
    setSelectedThreadTitle(title);
    setShowDeleteModal(true);
  };

  const closeModals = () => {
    setShowRenameModal(false);
    setShowDeleteModal(false);
    setSelectedThreadId(undefined);
    setSelectedThreadTitle(undefined);
  };

  if (isLoadingThreads) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {threads.length === 0 ? (
        <div className="text-center py-8">
          <MessageCircle className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No conversations yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Start a new chat to begin
          </p>
        </div>
      ) : (
        threads.map((thread) => (
          <ThreadItem
            key={thread.id}
            thread={thread}
            isSelected={currentThread?.id === thread.id}
            onClick={() => handleThreadClick(thread)}
            onRename={handleRename}
            onDelete={handleDelete}
          />
        ))
      )}

      {/* Rename Modal */}
      <RenameThreadModal
        isOpen={showRenameModal}
        onClose={closeModals}
        threadId={selectedThreadId}
        currentTitle={selectedThreadTitle}
      />

      {/* Delete Modal */}
      <DeleteThreadModal
        isOpen={showDeleteModal}
        onClose={closeModals}
        threadId={selectedThreadId}
        threadTitle={selectedThreadTitle}
      />
    </div>
  );
};

export default ThreadList;